/**
 * Dashboard-side labels surface — re-exports the pure-string functions
 * from `@sill/audit/labels` and adds the React-coupled icon helpers.
 *
 * ADR-0035 §3: `AGENT_DISPLAY_NAME`, `displayAgentName`, `friendlyActionLabel`,
 * `summarizeIntent`, `recordHeadline`, and the rest of the string-only
 * functions moved to `packages/audit/src/labels.ts` so the bundle route
 * (`services/api/src/http/routes/v1/audit-bundle.ts`) and the dashboard
 * project the same audit-record shape into the same human-readable text.
 *
 * `providerIcon`, `visitorClassIcon`, and `RULE_ACTION_TOOLTIP` stay here:
 * they take a React / lucide-react dependency that has no business in
 * `packages/audit/` (the bundle HTML renders class-level inline glyphs,
 * not lucide React components).
 */

import {
  Apple,
  Bot,
  Globe,
  Hexagon,
  MessageCircle,
  Search,
  Sparkles,
  Star,
  Telescope,
  User,
  type LucideIcon,
} from 'lucide-react';

import type { RuleResultAction } from '@sill/types';

// Re-export the moved string functions so existing dashboard imports
// (`./intent-label.js`) keep working unchanged. The underlying source
// lives in `@sill/audit/labels`.
export {
  AGENT_ACTIVITY,
  AGENT_BRAND_NAME,
  AGENT_DISPLAY_NAME,
  browserShortLabel,
  describeIntent,
  displayAgentName,
  botNameFromUserAgent,
  extractCompatibleBotName,
  formatIntentAmount,
  formatMinorAmount,
  friendlyActionLabel,
  inferAgentFromUserAgent,
  outcomeLabel,
  recordHeadline,
  settledTotalForHeadline,
  shortAgentDisplayName,
  summarizeIntent,
  uaFirstToken,
} from '@sill/audit/labels';

// ---------------------------------------------------------------------------
// Spoof signal classifier — mirrors the server-side function in
// `services/api/src/http/routes/public/crawl-event.ts` so the dashboard
// can flag suspicious UAs on list rows and detail panels without waiting
// for a backend field.
// ---------------------------------------------------------------------------

import { publicSignerDisplayFor } from '@sill/types';
import { isSillCredentialSigner } from '@sill/webbotauth';

export type SpoofSignal = 'empty_ua' | 'bare_ua' | 'typo_ua' | 'cf_unverified' | null;

/**
 * Compute a spoof signal from the UA, optional CF verification flag and
 * optional signature state. Returns null when no signal is present
 * (clean visit). Only meaningful for non-human visits; pass
 * visitorClass to skip humans.
 *
 * Server parity (`services/api/src/http/routes/public/crawl-event.ts`
 * `computeSpoofSignal`, ADR-0229 §4): `cf_unverified` only when the UA
 * CLAIMS a registered agent (`visitorClass === 'matched_agent'`).
 * Cloudflare reports `verifiedBot=false` for every bot outside its list
 * (curl, unlisted crawlers, every Sill-enrolled operator); without the
 * guard the dashboard flagged nearly every row.
 *
 * Security review 2026-10-11 MINOR 2: a verified signature by itself is
 * NOT enough to suppress the IP-mismatch warning. A valid signature
 * only proves control of the signer host; it does not vouch for the
 * UA. The warning is suppressed ONLY when the signature can be trusted
 * for THIS UA:
 *
 *   (a) Sill signer AND the operator registered this UA as one of its
 *       approved product tokens (`signedCredentialNameRegistered === true`), or
 *   (b) a KNOWN_PUBLIC_SIGNERS entry whose `allowed_token_re` matches
 *       the UA product token.
 *
 * Everything else — verified signature with no name_registered flag,
 * verified public signer whose UA does not match the signer's documented
 * pattern — keeps the heuristic and may still earn `cf_unverified`.
 */
export function computeSpoofSignal(
  ua: string | undefined,
  cfVerifiedBot: boolean | undefined | null,
  visitorClass?: 'matched_agent' | 'unknown_agent' | 'human_likely',
  signatureVerified?: boolean | null,
  signedAgent?: string | null,
  signedCredentialNameRegistered?: boolean | null,
): SpoofSignal {
  if (visitorClass === 'human_likely') return null;
  // ADR-0214 — a verified Web Bot Auth signature is a strong identity
  // signal, but ONLY when it covers (signer, UA). See the function
  // docstring for the conservative gate.
  if (signatureVerified === true) {
    const signer = signedAgent ?? null;
    const sillTrusted =
      isSillCredentialSigner(signer) && signedCredentialNameRegistered === true;
    const publicTrusted =
      !isSillCredentialSigner(signer)
      && publicSignerDisplayFor(signer, ua ?? null) !== null;
    if (sillTrusted || publicTrusted) return null;
  }
  const s = (ua ?? '').trim();
  if (s.length === 0) return 'empty_ua';
  if (/^Mozli[^l]/i.test(s) || /^Mozzila/i.test(s)) return 'typo_ua';
  if (/^Mozilla\/\d+\.\d+\s*$/.test(s)) return 'bare_ua';
  // ADR-0229 §4 — only a UA that claims a registered agent can earn
  // the `cf_unverified` marker. Otherwise `cf_verified_bot = false` just
  // means Cloudflare does not know this bot (curl, SillReadiness, an
  // enrolled operator whose IP ranges Sill does not publish).
  if (cfVerifiedBot === false && visitorClass === 'matched_agent') return 'cf_unverified';
  return null;
}

/**
 * Per-provider lucide-react icon for the matched-agent pill and the
 * MandateDetail header. Lucide doesn't ship the real OpenAI/Anthropic/
 * etc. brand marks (and using third-party brand logos has legal
 * footguns), so this maps each org to the lucide icon that best
 * captures its product vibe — distinctive enough at 11-13px to give
 * a glanceable brand cue without infringing on anyone's logo.
 *
 * Fall-through (unknown agent_id) returns `Bot` so we always render
 * SOMETHING in the pill's icon slot.
 */
export function providerIcon(agent_id: string | undefined): LucideIcon {
  if (agent_id === undefined) return Bot;
  if (agent_id.startsWith('agent_anthropic_')) return Sparkles;
  if (agent_id.startsWith('agent_openai_')) return MessageCircle;
  if (agent_id.startsWith('agent_google_')) return Star;
  if (agent_id.startsWith('agent_microsoft_')) return Search;
  if (agent_id.startsWith('agent_meta_')) return Globe;
  if (agent_id.startsWith('agent_apple_')) return Apple;
  if (agent_id.startsWith('agent_duckduckgo_')) return Bot;
  if (agent_id.startsWith('agent_mistral_')) return Hexagon;
  if (agent_id.startsWith('agent_perplexity')) return Telescope;
  // Social link-preview bots — generic Bot icon; differentiated by
  // display name on the pill / detail header.
  if (agent_id.startsWith('agent_x_')) return MessageCircle;
  if (agent_id.startsWith('agent_linkedin_')) return Globe;
  if (agent_id.startsWith('agent_slack_')) return Hexagon;
  if (agent_id.startsWith('agent_discord_')) return Globe;
  return Bot;
}

/**
 * Visitor-class icon for the row card + the MandateDetail header.
 * `matched_agent` falls through to per-provider; `unknown_agent`
 * gets a generic `Bot` (signals "investigate this"); `human_likely`
 * gets `User` (signals "a person, not an agent").
 *
 * 2026-06-04 MCP audit fix: when `visitor_class` is undefined/absent
 * but the record carries an `agent_id` (or the legacy `matched` flag
 * is true), fall through to `providerIcon(agent_id)` so historical
 * rows ingested before the server-side classifier populated
 * `visitor_class` for MCP tool calls still render the right brand
 * mark. Belt-and-suspenders alongside the backend fix that now sets
 * `visitor_class` for MCP records.
 */
export function visitorClassIcon(
  visitor_class: 'matched_agent' | 'unknown_agent' | 'human_likely' | undefined,
  agent_id: string | undefined,
  matched?: boolean,
): LucideIcon {
  if (visitor_class === 'matched_agent') return providerIcon(agent_id);
  if (visitor_class === 'unknown_agent') return Bot;
  if (visitor_class === 'human_likely') return User;
  // Visitor class absent — historical / pre-classifier rows. If we
  // have a brand-resolvable agent_id (or the legacy matched flag),
  // render the provider icon instead of the generic Bot.
  if (agent_id !== undefined && agent_id.length > 0) {
    return providerIcon(agent_id);
  }
  if (matched === true) return providerIcon(agent_id);
  return Bot;
}

// ---------------------------------------------------------------------------
// Reject-reason humanizer — closed enum (no PII) carried on
// `verification_rejected` records. Sourced from
// `packages/mandate/src/verify.ts` `VerifyMandateRejectReason` plus
// the pipeline-local reasons the consumer writes through. Surfaced
// on the MandateDetail panel's "Reject reason" row so the merchant
// sees WHY the signature failed without having to read enum strings.
// ---------------------------------------------------------------------------

const REJECT_REASON_FRIENDLY: Record<string, string> = {
  // Shape errors
  malformed:                 'Malformed envelope',
  oversize:                  'Envelope oversize',
  agent_id_malformed:        'Malformed agent_id',
  mandate_id_malformed:      'Malformed mandate_id',
  algorithm_unsupported:     'Unsupported signing algorithm',
  replay_window_too_short:   'Replay window too short',
  // Liveness errors
  expired:                   'Expired envelope',
  expired_envelope:          'Expired envelope',
  issued_in_future:          'Envelope issued in the future',
  // Protocol errors
  protocol_unsupported:      'Unsupported protocol',
  // Replay defense
  replay:                    'Replay detected',
  replay_detected:           'Replay detected',
  // Registry / identity errors
  agent_unknown:             'Unknown agent',
  key_unknown:               'Unknown signing key',
  agent_revoked:             'Agent revoked',
  identity_mismatch:         'Identity mismatch',
  // Signature errors
  signature_invalid:         'Invalid signature',
  // Infrastructure errors
  verification_unavailable:  'Verification unavailable',
};

/**
 * Humanize a sanitized `reject_reason` enum value for the dashboard's
 * detail panel. Falls back to title-cased snake_case for any future
 * enum value the dashboard hasn't been updated for yet — the row still
 * renders something readable even if the server widens the enum.
 */
export function friendlyRejectReason(reason: string): string {
  const known = REJECT_REASON_FRIENDLY[reason];
  if (known !== undefined) return known;
  return reason
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Rule trace tooltip text — one line per action_taken value.
// ---------------------------------------------------------------------------

export const RULE_ACTION_TOOLTIP: Record<RuleResultAction, string> = {
  allow:
    'The rule\'s predicate passed. The mandate satisfies this guardrail; '
    + 'evaluation continued to the next rule.',
  allow_exemption:
    'The rule\'s predicate failed, but the rule\'s `action_on_match` is '
    + '`allow` — the rule short-circuited the evaluator to approved as an '
    + 'explicit exemption.',
  reject:
    'The rule\'s predicate failed, and the rule\'s `action_on_match` is '
    + '`reject`. The mandate was rejected here; remaining rules were skipped.',
  escalate:
    'The rule\'s predicate failed, and the rule\'s `action_on_match` is '
    + '`escalate`. The mandate was queued for human review.',
  evaluator_error:
    'The rule could not be evaluated (budget exhausted, malformed params, '
    + 'or handler exception). The mandate fails closed and is recorded as '
    + 'rejected.',
  none:
    'No action was taken — typically because evaluation was aborted before '
    + 'this rule ran or the rule was disabled mid-policy.',
  enforced_at_origin:
    'This rule is configured in the policy but enforced at the origin rail '
    + 'executor (right before the charge), not by the edge evaluator — it '
    + 'needs Postgres state the edge can’t run (e.g. the user’s 24h spend). '
    + 'The edge passed it through; the origin gates it.',
};

// ---------------------------------------------------------------------------
// Rule result state — what a row in the rules list means to a merchant.
// ---------------------------------------------------------------------------

/** The evaluator's marker for rules that never ran because an earlier rule decided. */
export const RULE_NOT_CHECKED_REASON = 'not_evaluated_due_to_short_circuit';

export type RuleResultState = 'passed' | 'failed' | 'exempted' | 'not_checked';

/**
 * Collapse a signed rule result into the state a merchant reads. The record
 * stores `passed: false` both for the rule that stopped the request and for
 * every rule after it that was never run, so "not checked" is told apart by
 * the evaluator's short-circuit marker (or a `none` action that did not pass).
 */
export function ruleResultState(r: {
  readonly passed?: boolean;
  readonly action_taken?: string;
  readonly reason?: string;
  readonly outcome?: string;
}): RuleResultState {
  if (r.reason === RULE_NOT_CHECKED_REASON) return 'not_checked';
  if (r.action_taken === 'allow_exemption') return 'exempted';
  if (r.outcome === 'rejected' || r.outcome === 'escalated') return 'failed';
  if (r.passed === false) return r.action_taken === 'none' ? 'not_checked' : 'failed';
  return 'passed';
}

export const RULE_STATE_LABEL: Record<RuleResultState, string> = {
  passed: 'PASSED',
  failed: 'FAILED',
  exempted: 'EXEMPTED',
  not_checked: 'NOT CHECKED',
};

export const RULE_STATE_TOOLTIP: Record<RuleResultState, string> = {
  passed: 'Passed: the request met this rule.',
  failed: 'Failed: the request broke this rule, so the rule\'s action (escalate or reject) decided the outcome.',
  exempted: 'Exempted: this rule matched and approved the request as an exception, so later rules were not checked.',
  not_checked: 'Not checked: an earlier rule already decided this request, so this rule never ran. It neither passed nor failed.',
};
